import React, { useEffect, useMemo, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";

import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "../assets/styles/custom-calendar.css";
import SidebarModal from "./SidebarModal";

const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

const getRandomColor = () => {
  const colors = [
    "#f87171", // red-400
    "#60a5fa", // blue-400
    "#34d399", // green-400
    "#fbbf24", // yellow-400
    "#a78bfa", // purple-400
    "#fb923c", // orange-400
    "#38bdf8", // sky-400
    "#f472b6", // pink-400
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

const testData = [
  {
    title: "Product Design Sprint",
    start: moment().startOf("day").toDate(),
    end: moment().add(1, "days").endOf("day").toDate(),
    color: getRandomColor(),
  },
  {
    title: "Marketing Strategy Meeting",
    start: moment().add(2, "days").toDate(),
    end: moment().add(2, "days").toDate(),
    color: getRandomColor(),
  },
  {
    title: "Frontend Development Sprint",
    start: moment().subtract(3, "days").toDate(),
    end: moment().add(1, "days").toDate(),
    color: getRandomColor(),
  },
  {
    title: "Sales Team Check-in",
    start: moment().add(5, "days").toDate(),
    end: moment().add(5, "days").toDate(),
    color: getRandomColor(),
  },
  {
    title: "Backend System Review",
    start: moment().add(7, "days").toDate(),
    end: moment().add(9, "days").toDate(),
    color: getRandomColor(),
  },
  {
    title: "Release Planning",
    start: moment().add(10, "days").toDate(),
    end: moment().add(10, "days").toDate(),
    color: getRandomColor(),
  },
  {
    title: "Internal Demo",
    start: moment().subtract(5, "days").toDate(),
    end: moment().subtract(5, "days").toDate(),
    color: getRandomColor(),
  },
  {
    title: "QA Testing Phase 1",
    start: moment().add(14, "days").toDate(),
    end: moment().add(17, "days").toDate(),
    color: getRandomColor(),
  },
  {
    title: "Support Team Workshop",
    start: moment().add(3, "weeks").toDate(),
    end: moment().add(3, "weeks").add(1, "days").toDate(),
    color: getRandomColor(),
  },
  {
    title: "Annual Company Review",
    start: moment().add(1, "months").toDate(),
    end: moment().add(1, "months").add(2, "days").toDate(),
    color: getRandomColor(),
  },
];

const MyStyle = ({ selected }) => {
  let parsedData = null;
  if (selected?.calendar_drill) {
    if (typeof selected.calendar_drill === "object") {
      parsedData = selected.calendar_drill;
    } else {
      try {
        parsedData = JSON.parse(selected.calendar_drill);
      } catch (e) {
        console.error("Failed to parse calendar_drill data", e);
      }
    }
  }

  const sectionKeys = parsedData ? Object.keys(parsedData) : [];
  const getTitleFromKey = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

  const startDate = selected?.start
    ? moment(selected.start).format("MMM DD, YYYY · hh:mm A")
    : null;
  const endDate = selected?.end
    ? moment(selected.end).format("MMM DD, YYYY · hh:mm A")
    : null;

  return (
    <div className="pt-2 text-gray-800 space-y-5">
      {/* Event Header Card */}
      <div className="bg-gradient-to-r from-gray-50 to-white p-4 rounded-xl border border-gray-200">
        <div className="flex items-center gap-2 mb-2">
          <span
            className="w-3.5 h-3.5 rounded-full inline-block flex-shrink-0 shadow-sm"
            style={{ backgroundColor: selected?.color || "#3b82f6" }}
          />
          <h3 className="text-lg font-bold text-gray-900 leading-snug">
            {selected?.title || "Event Details"}
          </h3>
        </div>

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-600">
          {startDate && (
            <div className="bg-white p-2.5 rounded-lg border border-gray-100 shadow-2xs">
              <span className="font-semibold text-gray-400 block text-[10px] uppercase tracking-wider mb-0.5">
                Start
              </span>
              <span className="font-medium text-gray-700">{startDate}</span>
            </div>
          )}
          {endDate && (
            <div className="bg-white p-2.5 rounded-lg border border-gray-100 shadow-2xs">
              <span className="font-semibold text-gray-400 block text-[10px] uppercase tracking-wider mb-0.5">
                End
              </span>
              <span className="font-medium text-gray-700">{endDate}</span>
            </div>
          )}
        </div>
      </div>

      {/* Drilldown sections if present */}
      {sectionKeys.map((sectionKey) => {
        const sectionValues = parsedData[sectionKey] || {};
        return (
          <div
            key={sectionKey}
            className="bg-white rounded-xl border border-gray-200 p-4 shadow-2xs"
          >
            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 pb-2.5 border-b border-gray-100 mb-3">
              {getTitleFromKey(sectionKey)}
            </h4>
            <div className="space-y-2">
              {Object.entries(sectionValues).map(([key, value]) => (
                <div
                  key={key}
                  className="flex items-center justify-between gap-4 text-sm py-1 border-b border-gray-50 last:border-none"
                >
                  <span className="text-gray-500 text-xs font-medium uppercase tracking-wide">
                    {key.replace(/_/g, " ")}
                  </span>
                  <span className="font-semibold text-gray-800 text-right">
                    {String(value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Fallback if no drilldown details exist */}
      {(!parsedData || sectionKeys.length === 0) && (
        <div className="text-center py-6 text-gray-400 text-sm bg-gray-50 rounded-xl border border-dashed border-gray-200">
          No additional drilldown records for this event.
        </div>
      )}
    </div>
  );
};

function CalenderControl1({
  setLoading,
  name,
  options,
  selectedCalenderControl,
}) {
  options = selectedCalenderControl?.options ?? options;

  console.log("the options are", options, selectedCalenderControl);

  const calenderData = useMemo(() => (options ?? [])[0] ?? {}, [options]);

  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [inProp, setInProp] = useState(false);

  const transformEvents = (backendEvents) => {
    return Array.isArray(backendEvents)
      ? backendEvents.map((event, idx) => ({
          ...event,
          id: event.id ?? idx,
          start: new Date(event.start),
          end: new Date(event.end),
        }))
      : [];
  };

  useEffect(() => {
    const transformed = transformEvents(calenderData?.data);
    setEvents(transformed);
  }, [calenderData]);

  // Fallback to demo testData only if backend data has not been provided
  const displayEvents =
    events && events.length > 0
      ? events
      : calenderData?.data
        ? events
        : testData;

  const onEventResize = ({ event, start, end }) => {
    setEvents((prevEvents) => {
      const targetList = prevEvents.length > 0 ? prevEvents : testData;
      return targetList.map((e) =>
        e === event || (e.title === event.title && e.start === event.start)
          ? { ...e, start, end }
          : e,
      );
    });
  };

  const onEventDrop = ({ event, start, end }) => {
    setEvents((prevEvents) => {
      const targetList = prevEvents.length > 0 ? prevEvents : testData;
      return targetList.map((e) =>
        e === event || (e.title === event.title && e.start === event.start)
          ? { ...e, start, end }
          : e,
      );
    });
  };

  const onSelect = (item) => {
    console.log("does this onselect work", item);
    setSelected(item);
    setInProp(true);
  };

  return (
    <>
      <div className="custom-calendar-container bg-white rounded-2xl border border-gray-200/90 shadow-sm p-3 sm:p-5 md:p-6 transition-all">
        {name && (
          <div className="mb-4 pb-2 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900 tracking-tight">
              {name}
            </h2>
          </div>
        )}
        <DnDCalendar
          defaultDate={moment().toDate()}
          defaultView="month"
          views={["month", "week", "day", "agenda"]}
          events={displayEvents}
          localizer={localizer}
          onEventDrop={onEventDrop}
          onEventResize={onEventResize}
          onSelectEvent={(event) => onSelect(event)}
          resizable
          eventPropGetter={(event) => ({
            style: {
              backgroundColor: event.color || "#3b82f6",
              color: "#fff",
              borderRadius: "6px",
              padding: "2px 6px",
              border: "none",
            },
          })}
        />
      </div>
      {selected && (
        <SidebarModal inProp={inProp} closeSidebar={() => setInProp(false)}>
          <MyStyle selected={selected} />
        </SidebarModal>
      )}
    </>
  );
}

export default CalenderControl1;
